import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend
} from 'recharts';
import HomeIcon from '@mui/icons-material/Home';
import TokenIcon from '@mui/icons-material/Token';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MemoryIcon from '@mui/icons-material/Memory';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { auth } from '../firebase';

const API_URL = 'https://turismo.nara.chat/api';

const ConsumoTokens = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mes');
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState('');
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(localStorage.getItem('user_id'));
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeAgo, setTimeAgo] = useState('');
  // Pricing management state
  const [showPrecios, setShowPrecios] = useState(false);
  const [precios, setPrecios] = useState([]);
  const [preciosLoading, setPreciosLoading] = useState(false);
  const [editingPrecio, setEditingPrecio] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingPrecio, setSavingPrecio] = useState(false);

  // Check auth
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      if (!userId && user.uid) {
        try {
          const response = await fetch(`${API_URL}/users/by_uid/${user.uid}`);
          if (response.ok) {
            const data = await response.json();
            if (data.id) {
              localStorage.setItem('user_id', data.id);
              setUserId(data.id);
            }
          }
        } catch (err) {
          console.error('Error obteniendo user_id:', err);
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, userId]);

  // Fetch data
  useEffect(() => {
    if (userId) {
      fetchConsumo();
    }
  }, [periodo, empresaSeleccionada, userId]);

  // Auto-refresh every 60 seconds (silent — no spinner)
  useEffect(() => {
    if (!userId) return;
    const interval = setInterval(() => {
      fetchConsumo(true);
    }, 60000);
    return () => clearInterval(interval);
  }, [periodo, empresaSeleccionada, userId]);

  // Update "time ago" label every 15 seconds
  useEffect(() => {
    if (!lastUpdated) return;
    const updateAgo = () => {
      const diff = Math.floor((Date.now() - lastUpdated) / 1000);
      if (diff < 10) setTimeAgo('Justo ahora');
      else if (diff < 60) setTimeAgo(`Hace ${diff}s`);
      else setTimeAgo(`Hace ${Math.floor(diff / 60)} min`);
    };
    updateAgo();
    const interval = setInterval(updateAgo, 15000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  const fetchConsumo = async (silent = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ periodo });
      if (empresaSeleccionada) {
        params.append('empresa_id', empresaSeleccionada);
      }

      const response = await fetch(
        `${API_URL}/admin/consumo-tokens?${params}`,
        {
          headers: {
            'X-User-Id': userId,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 403) {
        setError('No tienes permisos para ver esta página');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setDatos(data);
      setLastUpdated(Date.now());
    } catch (err) {
      console.error('Error fetching consumo:', err);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // ===== FORMAT HELPERS =====
  const formatTokens = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatCosto = (num) => `$${num.toFixed(2)}`;

  // ===== COLOR HELPERS =====
  const getModelColor = (modelo) => {
    const colors = {
      'gpt-4o': '#06b6d4',
      'gpt-4o-mini': '#6366f1',
      'gpt-4': '#8b5cf6',
      'gpt-3.5-turbo': '#22d3ee',
      'whisper-1': '#14b8a6',
      'text-embedding-3-small': '#f97316',
      'claude-3-5-sonnet-20241022': '#f59e0b',
      'claude-sonnet-4-20250514': '#fbbf24',
      'claude-3-opus': '#ec4899',
      'claude-3-sonnet': '#f97316',
      'claude-3-haiku': '#fb923c',
      'gemini-pro': '#3b82f6',
      'gemini-pro-vision': '#60a5fa',
      'whatsapp': '#25D366',
      'whatsapp-inbound': '#128C7E',
      'whatsapp-outbound': '#25D366'
    };
    return colors[modelo] || '#64748b';
  };

  const getProviderChipStyle = (proveedor) => {
    const styles = {
      'openai': { bgcolor: 'rgba(6, 182, 212, 0.1)', color: '#0891b2' },
      'anthropic': { bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#d97706' },
      'twilio': { bgcolor: 'rgba(37, 211, 102, 0.1)', color: '#128C7E' },
      'google': { bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#2563eb' }
    };
    return styles[proveedor] || { bgcolor: 'rgba(100, 116, 139, 0.1)', color: '#64748b' };
  };

  // ===== SERVICE CATEGORY MAPPING =====
  const MODEL_TO_CATEGORY = {
    'gpt-4o': 'IA Generativa',
    'gpt-4o-mini': 'IA Generativa',
    'gpt-4': 'IA Generativa',
    'gpt-3.5-turbo': 'IA Generativa',
    'whisper-1': 'IA Generativa',
    'claude-3-opus': 'IA Generativa',
    'claude-3-sonnet': 'IA Generativa',
    'claude-3-haiku': 'IA Generativa',
    'claude-sonnet-4-20250514': 'IA Generativa',
    'claude-3-5-sonnet-20241022': 'IA Generativa',
    'gemini-pro': 'IA Generativa',
    'gemini-pro-vision': 'IA Generativa',
    'text-embedding-3-small': 'Embeddings',
    'whatsapp': 'Mensajeria',
    'whatsapp-inbound': 'Mensajeria',
    'whatsapp-outbound': 'Mensajeria'
  };

  const CATEGORY_COLORS = {
    'IA Generativa': '#6366f1',
    'Embeddings': '#f97316',
    'Mensajeria': '#25D366'
  };

  const PROVIDER_LABELS = {
    'openai': 'OpenAI',
    'anthropic': 'Anthropic',
    'google': 'Google',
    'twilio': 'Twilio'
  };

  // ===== COMPUTED DATA =====
  const getDonutData = () => {
    if (!datos?.por_modelo) return [];
    const categoryMap = {};
    datos.por_modelo.forEach((m) => {
      const cat = MODEL_TO_CATEGORY[m.modelo] || 'IA Generativa';
      if (!categoryMap[cat]) categoryMap[cat] = 0;
      categoryMap[cat] += m.costo;
    });
    // Add fixed WhatsApp number cost + overhead to Mensajeria
    const extraMensajeria = (datos.resumen?.costo_fijo_numeros || 0) + (datos.resumen?.overhead_twilio || 0);
    if (extraMensajeria > 0) {
      if (!categoryMap['Mensajeria']) categoryMap['Mensajeria'] = 0;
      categoryMap['Mensajeria'] += extraMensajeria;
    }
    return Object.entries(categoryMap)
      .map(([name, value]) => ({
        name,
        value: parseFloat(value.toFixed(4)),
        color: CATEGORY_COLORS[name] || '#64748b'
      }))
      .filter(d => d.value > 0);
  };

  const getProyeccion = () => {
    const fijos = (datos?.resumen?.costo_fijo_numeros || 0) + (datos?.resumen?.overhead_twilio || 0);
    if (!datos?.resumen || periodo !== 'mes') return (datos?.resumen?.costo_estimado || 0) + fijos;
    const now = new Date();
    const dayOfMonth = now.getDate();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    if (dayOfMonth === 0) return datos.resumen.costo_estimado + fijos;
    // Variable costs are projected, fixed/overhead stay constant for the month
    return (datos.resumen.costo_estimado / dayOfMonth) * daysInMonth + fijos;
  };

  // ===== CSV EXPORT =====
  const exportCSV = () => {
    if (!datos?.por_modelo || datos.por_modelo.length === 0) return;
    const headers = ['Proveedor', 'Modelo', 'Categoria', 'Tokens Input', 'Tokens Output', 'Tokens Total', 'Costo USD'];
    const rows = datos.por_modelo.map((m) => [
      m.proveedor,
      m.modelo,
      MODEL_TO_CATEGORY[m.modelo] || 'IA Generativa',
      m.tokens_input || 0,
      m.tokens_output || 0,
      m.tokens,
      m.costo.toFixed(4)
    ]);
    const csv = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${v}"`).join(','))
    ].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consumo_servicios_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ===== PRECIOS MANAGEMENT =====
  const fetchPrecios = async () => {
    setPreciosLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/precios`, {
        headers: { 'X-User-Id': userId }
      });
      if (response.ok) {
        const data = await response.json();
        setPrecios(data.precios || []);
      }
    } catch (err) {
      console.error('Error fetching precios:', err);
    } finally {
      setPreciosLoading(false);
    }
  };

  const handleEditPrecio = (precio) => {
    setEditingPrecio(precio.id);
    setEditValues({
      precio_input: precio.precio_input,
      precio_output: precio.precio_output,
      descripcion: precio.descripcion || ''
    });
  };

  const handleSavePrecio = async (precioId) => {
    setSavingPrecio(true);
    try {
      const response = await fetch(`${API_URL}/admin/precios/${precioId}`, {
        method: 'PUT',
        headers: { 'X-User-Id': userId, 'Content-Type': 'application/json' },
        body: JSON.stringify(editValues)
      });
      if (response.ok) {
        setEditingPrecio(null);
        fetchPrecios();
        // Refresh consumo data to reflect new prices
        fetchConsumo();
      }
    } catch (err) {
      console.error('Error saving precio:', err);
    } finally {
      setSavingPrecio(false);
    }
  };

  const handleToggleActivo = async (precio) => {
    try {
      await fetch(`${API_URL}/admin/precios/${precio.id}`, {
        method: 'PUT',
        headers: { 'X-User-Id': userId, 'Content-Type': 'application/json' },
        body: JSON.stringify({ activo: !precio.activo })
      });
      fetchPrecios();
    } catch (err) {
      console.error('Error toggling precio:', err);
    }
  };

  const getTipoLabel = (tipo) => {
    const labels = { 'por_1k_tokens': '/1K tokens', 'por_unidad': '/unidad', 'porcentaje': '%' };
    return labels[tipo] || tipo;
  };

  const getPrecioDisplay = (precio) => {
    if (precio.tipo === 'porcentaje') return `${precio.precio_output}%`;
    if (precio.tipo === 'por_unidad') return `$${precio.precio_output.toFixed(2)}`;
    // por_1k_tokens
    const parts = [];
    if (precio.precio_input > 0) parts.push(`In: $${precio.precio_input}`);
    if (precio.precio_output > 0) parts.push(`Out: $${precio.precio_output}`);
    return parts.join(' · ') || '$0';
  };

  // ===== COMPARATIVA VS PERIODO ANTERIOR =====
  const getVariacion = (actual, anterior) => {
    if (!anterior || anterior === 0) return null;
    const pct = ((actual - anterior) / anterior) * 100;
    return { pct: Math.round(pct), up: pct > 0 };
  };

  const periodoAnteriorLabel = { mes: 'vs mes anterior', trimestre: 'vs trim. anterior', año: 'vs año anterior' };

  // ===== INSIGHTS AUTOMÁTICOS =====
  const getInsights = () => {
    if (!datos?.por_modelo || datos.por_modelo.length === 0) return [];
    const insights = [];
    const totalCosto = datos.resumen.costo_estimado;

    // 1. Modelo dominante
    if (datos.por_modelo.length > 1 && totalCosto > 0) {
      const top = datos.por_modelo[0];
      const pct = Math.round((top.costo / totalCosto) * 100);
      if (pct >= 50) {
        insights.push({
          text: `${top.modelo} representa el ${pct}% del costo total`,
          color: '#6366f1'
        });
      }
    }

    // 2. Variación de costo vs periodo anterior
    const varCosto = getVariacion(totalCosto, datos.resumen.prev_costo);
    if (varCosto && Math.abs(varCosto.pct) >= 10) {
      insights.push({
        text: `Costo ${varCosto.up ? 'subió' : 'bajó'} ${Math.abs(varCosto.pct)}% ${periodoAnteriorLabel[periodo]}`,
        color: varCosto.up ? '#ef4444' : '#10b981'
      });
    }

    // 3. Chatbot con más consumo (si hay datos por chatbot)
    if (datos.por_chatbot?.length > 1) {
      const chatbotMap = {};
      datos.por_chatbot.forEach((r) => {
        const k = r.chatbot_name;
        chatbotMap[k] = (chatbotMap[k] || 0) + r.costo;
      });
      const sorted = Object.entries(chatbotMap).sort((a, b) => b[1] - a[1]);
      if (sorted.length > 1) {
        const avg = sorted.reduce((s, [, c]) => s + c, 0) / sorted.length;
        if (sorted[0][1] > avg * 2) {
          insights.push({
            text: `${sorted[0][0]} consume ${(sorted[0][1] / avg).toFixed(1)}x más que el promedio`,
            color: '#f59e0b'
          });
        }
      }
    }

    return insights.slice(0, 3);
  };

  const periodos = [
    { value: 'mes', label: 'Este mes' },
    { value: 'trimestre', label: 'Trimestre' },
    { value: 'año', label: 'Este año' }
  ];

  // ===== SUB-COMPONENTS =====

  // KPI Card
  const KPICard = ({ titulo, valor, subtitulo, icono, gradiente, shadowColor, variacion }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: '#fff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: gradiente
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 3,
            background: gradiente,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 20px ${shadowColor}`,
            flexShrink: 0,
            '& svg': { color: '#fff', fontSize: 24 }
          }}
        >
          {icono}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontSize: 26,
                fontWeight: 800,
                background: gradiente,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1.1
              }}
            >
              {valor}
            </Typography>
            {variacion && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.25,
                px: 0.75, py: 0.25, borderRadius: 1,
                bgcolor: variacion.up ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
              }}>
                {variacion.up
                  ? <ArrowUpwardIcon sx={{ fontSize: 12, color: '#ef4444' }} />
                  : <ArrowDownwardIcon sx={{ fontSize: 12, color: '#10b981' }} />
                }
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: variacion.up ? '#ef4444' : '#10b981' }}>
                  {Math.abs(variacion.pct)}%
                </Typography>
              </Box>
            )}
          </Box>
          <Typography sx={{ color: '#1e293b', fontWeight: 600, fontSize: 12 }}>
            {titulo}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 11 }}>
            {subtitulo}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  // Model Bar Component
  const ModelBar = ({ modelo, proveedor, tokens, porcentaje, costo }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: getModelColor(modelo),
              flexShrink: 0
            }}
          />
          <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {modelo === 'whatsapp-inbound' ? 'WhatsApp Inbound' : modelo === 'whatsapp-outbound' ? 'WhatsApp Outbound' : modelo}
          </Typography>
          <Chip
            label={proveedor}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              ...getProviderChipStyle(proveedor)
            }}
          />
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 13 }}>
            {formatCosto(costo)}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 10 }}>
            {formatTokens(tokens)} {modelo.startsWith('whatsapp') ? 'mensajes' : 'tokens'}
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={porcentaje}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(0, 0, 0, 0.06)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            bgcolor: getModelColor(modelo)
          }
        }}
      />
      <Typography sx={{ color: '#94a3b8', fontSize: 10, textAlign: 'right', mt: 0.25 }}>
        {porcentaje}%
      </Typography>
    </Box>
  );

  // Company Card
  const EmpresaCard = ({ empresa, onClick }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 3,
        background: empresaSeleccionada === empresa.id ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.95)',
        border: empresaSeleccionada === empresa.id ? '2px solid #6366f1' : '1px solid rgba(0, 0, 0, 0.06)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>
          {empresa.nombre}
        </Typography>
        <Chip
          label={`${empresa.chatbots} chatbots`}
          size="small"
          sx={{ height: 20, fontSize: 10, bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}
        />
      </Box>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#10b981', mb: 0.5 }}>
        {formatCosto(empresa.costo)}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ color: '#64748b', fontSize: 11 }}>
          {formatTokens(empresa.tokens)} tokens
        </Typography>
        <Typography sx={{ color: '#94a3b8', fontSize: 11 }}>
          {empresa.porcentaje}% del total
        </Typography>
      </Box>
    </Paper>
  );

  // Custom donut label
  const renderDonutLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#1e293b" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight={600}>
        ${value.toFixed(2)}
      </text>
    );
  };

  // Custom tooltip for stacked chart
  const CustomTrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const providers = payload.filter(p => p.dataKey !== 'acumulado');
    const acumEntry = payload.find(p => p.dataKey === 'acumulado');
    const total = providers.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
    return (
      <Box sx={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        p: 1.5,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 13, mb: 0.5 }}>{label}</Typography>
        {providers.filter(p => Number(p.value) > 0).map((p, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.fill || p.stroke, flexShrink: 0 }} />
            <Typography sx={{ color: '#64748b', fontSize: 11, flex: 1 }}>
              {PROVIDER_LABELS[p.dataKey] || p.dataKey}
            </Typography>
            <Typography sx={{ color: '#1e293b', fontSize: 11, fontWeight: 600 }}>
              ${Number(p.value).toFixed(4)}
            </Typography>
          </Box>
        ))}
        <Box sx={{ borderTop: '1px solid #e2e8f0', mt: 0.5, pt: 0.5 }}>
          <Typography sx={{ color: '#1e293b', fontSize: 12, fontWeight: 700, textAlign: 'right' }}>
            Día: ${total.toFixed(4)}
          </Typography>
        </Box>
        {acumEntry && Number(acumEntry.value) > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Box sx={{ width: 14, height: 2, bgcolor: '#8b5cf6', borderRadius: 1 }} />
            <Typography sx={{ color: '#8b5cf6', fontSize: 11, fontWeight: 700 }}>
              Acumulado: ${Number(acumEntry.value).toFixed(4)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f1f5f9',
        py: 3,
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#1e293b', fontWeight: 700 }}>
              Consumo de Servicios
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>
              Monitoreo de IA, mensajeria Twilio y embeddings
            </Typography>
          </Box>
        </Box>

        {/* Filters + Export */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Company selector */}
          {datos?.empresas_disponibles?.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={empresaSeleccionada}
                label="Empresa"
                onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                startAdornment={<FilterListIcon sx={{ color: '#94a3b8', mr: 1, fontSize: 18 }} />}
              >
                <MenuItem value="">Todas las empresas</MenuItem>
                {datos.empresas_disponibles.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* CSV Export */}
          <Tooltip title="Exportar CSV" arrow>
            <span>
              <IconButton
                onClick={exportCSV}
                disabled={!datos?.por_modelo?.length}
                sx={{ color: '#64748b', '&:hover': { color: '#1e293b' } }}
              >
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Period selector */}
          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#e2e8f0', borderRadius: 2, p: 0.5 }}>
            {periodos.map((p) => (
              <Button
                key={p.value}
                size="small"
                onClick={() => setPeriodo(p.value)}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  color: periodo === p.value ? '#1e293b' : '#64748b',
                  bgcolor: periodo === p.value ? '#fff' : 'transparent',
                  fontWeight: periodo === p.value ? 600 : 400,
                  fontSize: 13,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: periodo === p.value ? '#fff' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                {p.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#6366f1' }} />
        </Box>
      )}

      {/* Error */}
      {error && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', background: '#fff', borderRadius: 3 }}>
          <Typography sx={{ color: '#ef4444', fontWeight: 600, mb: 1 }}>
            {error}
          </Typography>
          <Button onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
            Volver al Dashboard
          </Button>
        </Paper>
      )}

      {/* Data loaded */}
      {!loading && datos && !error && (
        <>
          {/* Auto-refresh indicator */}
          {timeAgo && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mb: 1 }}>
              <AccessTimeIcon sx={{ color: '#94a3b8', fontSize: 14 }} />
              <Typography sx={{ color: '#94a3b8', fontSize: 11 }}>
                {timeAgo}
              </Typography>
            </Box>
          )}

          {/* Insights row */}
          {getInsights().length > 0 && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              {getInsights().map((insight, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 1.5, py: 0.75, borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: insight.color, fontSize: 16 }} />
                  <Typography sx={{ color: '#1e293b', fontSize: 12, fontWeight: 500 }}>
                    {insight.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* KPI Cards — 5 cards, cost-first */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
              gap: 2,
              mb: 3
            }}
          >
            {(() => {
              const costoTotal = datos.resumen.costo_estimado + (datos.resumen.costo_fijo_numeros || 0) + (datos.resumen.overhead_twilio || 0);
              const prevTotal = datos.resumen.prev_costo + (datos.resumen.costo_fijo_numeros || 0) + (datos.resumen.overhead_twilio || 0);
              const partes = [];
              partes.push(`Servicios ${formatCosto(datos.resumen.costo_estimado)}`);
              if (datos.resumen.costo_fijo_numeros > 0) partes.push(`Números $${datos.resumen.costo_fijo_numeros.toFixed(2)}`);
              if (datos.resumen.overhead_twilio > 0) partes.push(`Overhead $${datos.resumen.overhead_twilio.toFixed(2)}`);
              return (
                <KPICard
                  titulo="Costo Total"
                  valor={formatCosto(costoTotal)}
                  subtitulo={partes.length > 1 ? partes.join(' + ') : periodoAnteriorLabel[periodo]}
                  icono={<AttachMoneyIcon />}
                  gradiente="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                  shadowColor="rgba(16, 185, 129, 0.3)"
                  variacion={getVariacion(costoTotal, prevTotal)}
                />
              );
            })()}
            <KPICard
              titulo="Tokens Totales"
              valor={formatTokens(datos.resumen.total_tokens)}
              subtitulo={`${formatTokens(datos.resumen.total_input)} in / ${formatTokens(datos.resumen.total_output)} out`}
              icono={<TokenIcon />}
              gradiente="linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
              shadowColor="rgba(99, 102, 241, 0.3)"
              variacion={getVariacion(datos.resumen.total_tokens, datos.resumen.prev_tokens)}
            />
            <KPICard
              titulo="Empresas Activas"
              valor={datos.resumen.empresas_activas}
              subtitulo="Con consumo"
              icono={<BusinessIcon />}
              gradiente="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
              shadowColor="rgba(245, 158, 11, 0.3)"
            />
            <KPICard
              titulo="Servicios Usados"
              valor={datos.por_modelo.length}
              subtitulo="Diferentes proveedores"
              icono={<MemoryIcon />}
              gradiente="linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
              shadowColor="rgba(14, 165, 233, 0.3)"
            />
            <KPICard
              titulo="Proyeccion Mensual"
              valor={formatCosto(getProyeccion())}
              subtitulo={periodo === 'mes' ? 'Estimado fin de mes' : 'Basado en periodo actual'}
              icono={<TrendingUpIcon />}
              gradiente="linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
              shadowColor="rgba(249, 115, 22, 0.3)"
            />
          </Box>

          {/* Main Grid: 2 columns (modelo + donut) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            {/* Desglose por Servicio */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                background: '#fff',
                backdropFilter: 'blur(20px)',
                border: '1px solid #e2e8f0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <MemoryIcon sx={{ color: '#6366f1' }} />
                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>
                  Desglose por Servicio
                </Typography>
              </Box>
              {datos.por_modelo.length > 0 ? (
                <>
                  {datos.por_modelo.map((modelo, idx) => (
                    <ModelBar
                      key={idx}
                      modelo={modelo.modelo}
                      proveedor={modelo.proveedor}
                      tokens={modelo.tokens}
                      porcentaje={modelo.porcentaje}
                      costo={modelo.costo}
                    />
                  ))}
                  {/* WhatsApp Total (subtotal inbound + outbound) */}
                  {(() => {
                    const waModels = datos.por_modelo.filter(m => m.modelo === 'whatsapp-inbound' || m.modelo === 'whatsapp-outbound');
                    if (waModels.length < 2) return null;
                    const totalMsgs = waModels.reduce((s, m) => s + m.tokens, 0);
                    const totalCosto = waModels.reduce((s, m) => s + m.costo, 0);
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -0.5, mb: 1.5, ml: 2, px: 1.5, py: 0.75, borderRadius: 2, bgcolor: 'rgba(37, 211, 102, 0.06)', border: '1px solid rgba(37, 211, 102, 0.15)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: '#128C7E', fontSize: 11 }}>
                            WhatsApp Total
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography sx={{ color: '#128C7E', fontSize: 11, fontWeight: 500 }}>
                            {totalMsgs} mensajes
                          </Typography>
                          <Typography sx={{ fontWeight: 700, color: '#128C7E', fontSize: 12 }}>
                            {formatCosto(totalCosto)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                  {/* Costo fijo números WhatsApp */}
                  {datos.resumen.numeros_activos > 0 && (
                    <Box sx={{
                      mt: 1, pt: 1.5,
                      borderTop: '1px dashed rgba(0, 0, 0, 0.08)'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#64748b', flexShrink: 0 }} />
                          <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 12 }}>
                            Números WhatsApp
                          </Typography>
                          <Chip
                            label="Twilio"
                            size="small"
                            sx={{ height: 18, fontSize: 10, ...getProviderChipStyle('twilio') }}
                          />
                          <Chip
                            label="Costo fijo"
                            size="small"
                            sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(100, 116, 139, 0.1)', color: '#64748b', fontWeight: 600 }}
                          />
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 13 }}>
                            ${(datos.resumen.costo_fijo_numeros || 0).toFixed(2)}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: 10 }}>
                            {datos.resumen.numeros_activos} {datos.resumen.numeros_activos === 1 ? 'número' : 'números'} × $1.15/mes
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {/* Overhead estimado Twilio */}
                  {datos.resumen.overhead_twilio > 0 && (
                    <Box sx={{ mt: datos.resumen.numeros_activos > 0 ? 0.5 : 1, pt: datos.resumen.numeros_activos > 0 ? 0 : 1.5, borderTop: datos.resumen.numeros_activos > 0 ? 'none' : '1px dashed rgba(0, 0, 0, 0.08)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8', flexShrink: 0 }} />
                          <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 12 }}>
                            Overhead estimado
                          </Typography>
                          <Chip
                            label="Twilio"
                            size="small"
                            sx={{ height: 18, fontSize: 10, ...getProviderChipStyle('twilio') }}
                          />
                          <Chip
                            label={`${((datos.resumen.overhead_twilio_pct || 0.10) * 100).toFixed(0)}%`}
                            size="small"
                            sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(100, 116, 139, 0.1)', color: '#64748b', fontWeight: 600 }}
                          />
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 13 }}>
                            ${(datos.resumen.overhead_twilio || 0).toFixed(2)}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: 10 }}>
                            Meta fees + msgs fallidos
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>
                  No hay datos de consumo
                </Typography>
              )}
            </Paper>

            {/* Donut Chart — Costo por Categoria */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                background: '#fff',
                backdropFilter: 'blur(20px)',
                border: '1px solid #e2e8f0'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <DonutLargeIcon sx={{ color: '#8b5cf6' }} />
                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>
                  Costo por Categoria
                </Typography>
              </Box>
              {getDonutData().length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={getDonutData()}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={3}
                        dataKey="value"
                        nameKey="name"
                        label={renderDonutLabel}
                        labelLine={false}
                      >
                        {getDonutData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                        ))}
                      </Pie>
                      <ChartTooltip
                        contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}
                        formatter={(value) => [`$${value.toFixed(4)}`, 'Costo']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Legend manual */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                    {getDonutData().map((entry) => (
                      <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color }} />
                        <Typography sx={{ color: '#1e293b', fontSize: 11, fontWeight: 500 }}>
                          {entry.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* Category totals */}
                  <Box sx={{ mt: 2 }}>
                    {getDonutData().map((entry) => (
                      <Box key={entry.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: entry.color }} />
                          <Typography sx={{ color: '#475569', fontSize: 12, fontWeight: 500 }}>{entry.name}</Typography>
                        </Box>
                        <Typography sx={{ color: '#1e293b', fontSize: 13, fontWeight: 700 }}>${entry.value.toFixed(2)}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>
                  No hay datos de consumo
                </Typography>
              )}
            </Paper>
          </Box>

          {/* Tendencia de Costos — Full width */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 4,
              background: '#fff',
              backdropFilter: 'blur(20px)',
              border: '1px solid #e2e8f0'
            }}
          >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <TrendingUpIcon sx={{ color: '#10b981' }} />
                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>
                  {datos.tendencia_diaria ? 'Tendencia Diaria de Costos' : 'Tendencia de Costos'}
                </Typography>
                {datos.tendencia_diaria && (
                  <Chip label="Día a día" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 600, fontSize: 10, height: 20 }} />
                )}
              </Box>
              <ResponsiveContainer width="100%" height={280}>
                {(() => {
                  // Determine data source: daily (mes) vs monthly (trimestre/año)
                  const trendData = datos.tendencia_diaria || datos.tendencia_por_proveedor;
                  const xKey = datos.tendencia_diaria ? 'label' : 'mes_nombre';
                  // For daily: show fewer X labels to avoid overlap (every 5 days)
                  const tickInterval = datos.tendencia_diaria ? 4 : 0;

                  if (trendData) {
                    // Add cumulative field for daily view + distribute fixed number cost
                    let chartData = trendData;
                    const isDaily = !!datos.tendencia_diaria;
                    const costoFijoNumeros = datos.resumen?.costo_fijo_numeros || 0;
                    const overheadTwilio = datos.resumen?.overhead_twilio || 0;
                    const extraTwilioTotal = costoFijoNumeros + overheadTwilio;
                    if (isDaily) {
                      // Distribute monthly fixed+overhead cost evenly across days
                      const daysInMonth = trendData.length || 28;
                      const extraPerDay = extraTwilioTotal > 0 ? extraTwilioTotal / daysInMonth : 0;
                      let acum = 0;
                      chartData = trendData.map((d) => {
                        const twilioTotal = (d.twilio || 0) + extraPerDay;
                        acum += (d.openai || 0) + (d.anthropic || 0) + (d.google || 0) + twilioTotal;
                        return { ...d, twilio: parseFloat(twilioTotal.toFixed(4)), acumulado: parseFloat(acum.toFixed(4)) };
                      });
                    } else if (extraTwilioTotal > 0) {
                      // Monthly view: add fixed+overhead per month to months with activity
                      const numeros = datos.resumen?.numeros_activos || 0;
                      const fixedPerMonth = numeros * 1.15;
                      // Overhead scales with messaging, estimate same per active month
                      const activeMonths = trendData.filter(d => (d.openai || 0) + (d.anthropic || 0) + (d.google || 0) + (d.twilio || 0) > 0).length || 1;
                      const overheadPerMonth = overheadTwilio / activeMonths;
                      chartData = trendData.map((d) => {
                        const hasActivity = (d.openai || 0) + (d.anthropic || 0) + (d.google || 0) + (d.twilio || 0) > 0;
                        return hasActivity
                          ? { ...d, twilio: parseFloat(((d.twilio || 0) + fixedPerMonth + overheadPerMonth).toFixed(4)) }
                          : d;
                      });
                    }

                    return (
                      <ComposedChart data={chartData}>
                        <defs>
                          <linearGradient id="gradOpenai" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="gradAnthropic" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="gradGoogle" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="gradTwilio" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#25D366" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#25D366" stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} stroke="#94a3b8" fontSize={10} interval={tickInterval} angle={isDaily ? -45 : 0} textAnchor={isDaily ? 'end' : 'middle'} height={isDaily ? 45 : 30} />
                        <YAxis
                          yAxisId="left"
                          stroke="#94a3b8"
                          fontSize={11}
                          tickFormatter={(value) => `$${value}`}
                        />
                        {isDaily && (
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            stroke="#8b5cf6"
                            fontSize={11}
                            tickFormatter={(value) => `$${value}`}
                          />
                        )}
                        <ChartTooltip content={<CustomTrendTooltip />} />
                        <Area type="monotone" yAxisId="left" dataKey="openai" stackId="1" stroke="#06b6d4" strokeWidth={2} fill="url(#gradOpenai)" name="OpenAI" />
                        <Area type="monotone" yAxisId="left" dataKey="anthropic" stackId="1" stroke="#f59e0b" strokeWidth={2} fill="url(#gradAnthropic)" name="Anthropic" />
                        <Area type="monotone" yAxisId="left" dataKey="google" stackId="1" stroke="#3b82f6" strokeWidth={2} fill="url(#gradGoogle)" name="Google" />
                        <Area type="monotone" yAxisId="left" dataKey="twilio" stackId="1" stroke="#25D366" strokeWidth={2} fill="url(#gradTwilio)" name="Twilio" />
                        {isDaily && (
                          <Line type="monotone" yAxisId="right" dataKey="acumulado" stroke="#8b5cf6" strokeWidth={2.5} strokeDasharray="6 3" dot={false} name="Acumulado" />
                        )}
                      </ComposedChart>
                    );
                  }
                  return (
                    <AreaChart data={datos.tendencia}>
                      <defs>
                        <linearGradient id="gradCosto" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="mes_nombre" stroke="#94a3b8" fontSize={11} />
                      <YAxis
                        stroke="#94a3b8"
                        fontSize={11}
                        tickFormatter={(value) => `$${value}`}
                      />
                      <ChartTooltip
                        contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}
                        formatter={(value) => [`$${Number(value).toFixed(2)}`, 'Costo']}
                      />
                      <Area type="monotone" dataKey="costo" stroke="#6366f1" strokeWidth={2} fill="url(#gradCosto)" />
                    </AreaChart>
                  );
                })()}
              </ResponsiveContainer>
              {/* Legend for stacked */}
              {(datos.tendencia_diaria || datos.tendencia_por_proveedor) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                  {[
                    { key: 'openai', label: 'OpenAI', color: '#06b6d4' },
                    { key: 'anthropic', label: 'Anthropic', color: '#f59e0b' },
                    { key: 'google', label: 'Google', color: '#3b82f6' },
                    { key: 'twilio', label: 'Twilio', color: '#25D366' },
                    ...(datos.tendencia_diaria ? [{ key: 'acumulado', label: 'Acumulado', color: '#8b5cf6', dashed: true }] : [])
                  ].map((prov) => (
                    <Box key={prov.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        width: 14, height: 3, borderRadius: 1, bgcolor: prov.color,
                        ...(prov.dashed ? { backgroundImage: `repeating-linear-gradient(90deg, ${prov.color} 0px, ${prov.color} 4px, transparent 4px, transparent 7px)`, bgcolor: 'transparent' } : {})
                      }} />
                      <Typography sx={{ color: '#1e293b', fontSize: 11, fontWeight: 500 }}>
                        {prov.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
          </Paper>

          {/* Consumo por Empresa */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 4,
              background: '#fff',
              backdropFilter: 'blur(20px)',
              border: '1px solid #e2e8f0'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon sx={{ color: '#f59e0b' }} />
                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>
                  Consumo por Empresa
                </Typography>
              </Box>
              {empresaSeleccionada && (
                <Button
                  size="small"
                  onClick={() => setEmpresaSeleccionada('')}
                  sx={{ textTransform: 'none', color: '#6366f1' }}
                >
                  Ver todas
                </Button>
              )}
            </Box>
            {datos.por_empresa.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                  gap: 2
                }}
              >
                {datos.por_empresa.map((empresa) => (
                  <EmpresaCard
                    key={empresa.id}
                    empresa={empresa}
                    onClick={() => setEmpresaSeleccionada(empresa.id)}
                  />
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>
                No hay datos de consumo por empresa
              </Typography>
            )}
          </Paper>

          {/* Detalle por Chatbot (cuando hay empresa seleccionada) */}
          {empresaSeleccionada && datos.por_chatbot.length > 0 && (() => {
            // Group by chatbot
            const chatbotMap = {};
            datos.por_chatbot.forEach((row) => {
              const key = row.chatbot_id || row.chatbot_name;
              if (!chatbotMap[key]) {
                chatbotMap[key] = {
                  name: row.chatbot_name, totalCosto: 0, totalTokens: 0, costoTwilio: 0, modelos: [],
                  plan_name: row.plan_name || 'Sin plan',
                  precio_plan: row.precio_plan || 0,
                  costo_fijo_numero: row.costo_fijo_numero || 0
                };
              }
              chatbotMap[key].totalCosto += row.costo;
              chatbotMap[key].totalTokens += row.tokens;
              if (row.proveedor === 'twilio') chatbotMap[key].costoTwilio += row.costo;
              chatbotMap[key].modelos.push(row);
            });
            const chatbots = Object.values(chatbotMap).sort((a, b) => b.totalCosto - a.totalCosto);
            // Distribute overhead proportionally by Twilio messaging cost
            const totalTwilioChatbots = chatbots.reduce((s, b) => s + b.costoTwilio, 0);
            const overheadTotal = datos.resumen?.overhead_twilio || 0;
            chatbots.forEach((bot) => {
              bot.overhead = totalTwilioChatbots > 0 ? overheadTotal * (bot.costoTwilio / totalTwilioChatbots) : 0;
            });
            const maxCosto = chatbots.length > 0 ? chatbots[0].totalCosto : 1;

            return (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  background: '#fff',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid #e2e8f0'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <SmartToyIcon sx={{ color: '#8b5cf6' }} />
                  <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>
                    Costo por Chatbot
                  </Typography>
                  <Chip
                    label={datos.empresas_disponibles.find(e => e.id === empresaSeleccionada)?.nombre || 'Empresa'}
                    size="small"
                    sx={{ bgcolor: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {chatbots.map((bot, idx) => {
                    const costoTotal = bot.totalCosto + bot.costo_fijo_numero + bot.overhead;
                    const utilidad = bot.precio_plan - costoTotal;
                    const margen = bot.precio_plan > 0 ? (utilidad / bot.precio_plan) * 100 : 0;
                    const consumoPct = bot.precio_plan > 0 ? Math.min((costoTotal / bot.precio_plan) * 100, 100) : 0;
                    const barColor = consumoPct > 90 ? '#ef4444' : consumoPct > 70 ? '#f59e0b' : '#10b981';

                    return (
                      <Box
                        key={idx}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          border: '1px solid rgba(0, 0, 0, 0.06)',
                          background: 'rgba(248, 250, 252, 0.8)',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'rgba(139, 92, 246, 0.3)', background: 'rgba(248, 250, 252, 1)' }
                        }}
                      >
                        {/* Header: nombre + plan + costo */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                              width: 32, height: 32, borderRadius: 2,
                              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                              <SmartToyIcon sx={{ color: '#fff', fontSize: 18 }} />
                            </Box>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>
                                  {bot.name}
                                </Typography>
                                <Chip
                                  label={`${bot.plan_name} · $${bot.precio_plan.toFixed(0)}`}
                                  size="small"
                                  sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}
                                />
                              </Box>
                              <Typography sx={{ color: '#94a3b8', fontSize: 10 }}>
                                {bot.modelos.length} {bot.modelos.length === 1 ? 'servicio' : 'servicios'} · {formatTokens(bot.totalTokens)} tokens
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>
                              {formatCosto(costoTotal)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                              {bot.costo_fijo_numero > 0 ? 'costo total' : 'costo servicios'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Barra consumo vs plan */}
                        {bot.precio_plan > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>
                                Consumo vs Plan
                              </Typography>
                              <Typography sx={{ fontSize: 10, color: barColor, fontWeight: 700 }}>
                                {consumoPct.toFixed(1)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={consumoPct}
                              sx={{
                                height: 6, borderRadius: 3,
                                bgcolor: 'rgba(0, 0, 0, 0.06)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 3,
                                  bgcolor: barColor
                                }
                              }}
                            />
                          </Box>
                        )}

                        {/* Utilidad/Pérdida */}
                        {bot.precio_plan > 0 && (
                          <Box sx={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            p: 1.25, borderRadius: 2, mb: 1.5,
                            bgcolor: utilidad >= 0 ? 'rgba(16, 185, 129, 0.06)' : 'rgba(239, 68, 68, 0.06)',
                            border: `1px solid ${utilidad >= 0 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)'}`
                          }}>
                            <Box>
                              <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 500 }}>
                                {utilidad >= 0 ? 'Utilidad' : 'Pérdida'}
                              </Typography>
                              <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                                Plan ${bot.precio_plan.toFixed(2)} − Servicios {formatCosto(bot.totalCosto)}{bot.costo_fijo_numero > 0 ? ` − N° $${bot.costo_fijo_numero.toFixed(2)}` : ''}{bot.overhead > 0.001 ? ` − Overhead $${bot.overhead.toFixed(2)}` : ''}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography sx={{
                                fontWeight: 800, fontSize: 20,
                                color: utilidad >= 0 ? '#10b981' : '#ef4444'
                              }}>
                                {utilidad >= 0 ? '+' : ''}{formatCosto(utilidad)}
                              </Typography>
                              <Typography sx={{
                                fontSize: 10, fontWeight: 600,
                                color: utilidad >= 0 ? '#10b981' : '#ef4444'
                              }}>
                                {margen.toFixed(0)}% margen
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Desglose por modelo */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {bot.modelos.map((m, mIdx) => (
                            <Chip
                              key={mIdx}
                              label={`${m.modelo} · ${formatCosto(m.costo)}`}
                              size="small"
                              sx={{
                                height: 22, fontSize: 10, fontWeight: 600,
                                bgcolor: `${getModelColor(m.modelo)}15`,
                                color: getModelColor(m.modelo),
                                border: `1px solid ${getModelColor(m.modelo)}30`
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            );
          })()}

          {/* ===== CONFIGURAR PRECIOS (Superadmin) ===== */}
          <Paper
            sx={{
              mt: 3,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0, right: 0,
                height: 3,
                background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)'
              }
            }}
          >
            <Box
              onClick={() => {
                if (!showPrecios) {
                  setShowPrecios(true);
                  if (precios.length === 0) fetchPrecios();
                } else {
                  setShowPrecios(false);
                }
              }}
              sx={{
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 2,
                  background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(100, 116, 139, 0.3)'
                }}>
                  <SettingsIcon sx={{ color: '#fff', fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                    Configurar Precios de Servicios
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                    Ajusta costos de IA, mensajería y costos fijos
                  </Typography>
                </Box>
              </Box>
              <ExpandMoreIcon sx={{
                color: '#94a3b8',
                transform: showPrecios ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }} />
            </Box>

            {showPrecios && (
              <Box sx={{ px: 2.5, pb: 2.5 }}>
                {preciosLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <>
                    {/* Group by provider */}
                    {['openai', 'anthropic', 'google', 'twilio'].map((proveedor) => {
                      const provPrecios = precios.filter((p) => p.proveedor === proveedor);
                      if (provPrecios.length === 0) return null;

                      const provStyle = getProviderChipStyle(proveedor);

                      return (
                        <Box key={proveedor} sx={{ mb: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Chip
                              label={proveedor.charAt(0).toUpperCase() + proveedor.slice(1)}
                              size="small"
                              sx={{
                                height: 24, fontSize: 11, fontWeight: 700,
                                ...provStyle
                              }}
                            />
                            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                              {provPrecios.length} servicio{provPrecios.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>

                          <TableContainer sx={{
                            borderRadius: 2,
                            border: '1px solid rgba(0,0,0,0.06)',
                            '& .MuiTableCell-root': {
                              py: 1, px: 1.5, fontSize: 12, borderColor: 'rgba(0,0,0,0.04)'
                            }
                          }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                  <TableCell sx={{ fontWeight: 700, color: '#475569', width: '25%' }}>Servicio</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#475569', width: '12%' }}>Tipo</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#475569', width: '15%' }} align="right">Precio Input</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#475569', width: '15%' }} align="right">Precio Output</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Descripción</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#475569', width: '80px' }} align="center">Acciones</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {provPrecios.map((precio) => (
                                  <TableRow
                                    key={precio.id}
                                    sx={{
                                      opacity: precio.activo ? 1 : 0.4,
                                      '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.03)' }
                                    }}
                                  >
                                    {editingPrecio === precio.id ? (
                                      <>
                                        <TableCell>
                                          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#1e293b' }}>
                                            {precio.servicio}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={getTipoLabel(precio.tipo)}
                                            size="small"
                                            sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                                          />
                                        </TableCell>
                                        <TableCell align="right">
                                          <input
                                            type="number"
                                            step="0.000001"
                                            value={editValues.precio_input}
                                            onChange={(e) => setEditValues({ ...editValues, precio_input: parseFloat(e.target.value) || 0 })}
                                            style={{
                                              width: '100%', padding: '4px 8px', fontSize: 12,
                                              border: '1px solid #cbd5e1', borderRadius: 6,
                                              textAlign: 'right', outline: 'none'
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell align="right">
                                          <input
                                            type="number"
                                            step="0.000001"
                                            value={editValues.precio_output}
                                            onChange={(e) => setEditValues({ ...editValues, precio_output: parseFloat(e.target.value) || 0 })}
                                            style={{
                                              width: '100%', padding: '4px 8px', fontSize: 12,
                                              border: '1px solid #cbd5e1', borderRadius: 6,
                                              textAlign: 'right', outline: 'none'
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <input
                                            type="text"
                                            value={editValues.descripcion}
                                            onChange={(e) => setEditValues({ ...editValues, descripcion: e.target.value })}
                                            style={{
                                              width: '100%', padding: '4px 8px', fontSize: 12,
                                              border: '1px solid #cbd5e1', borderRadius: 6, outline: 'none'
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell align="center">
                                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleSavePrecio(precio.id)}
                                              disabled={savingPrecio}
                                              sx={{ color: '#10b981', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' } }}
                                            >
                                              <SaveIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              onClick={() => setEditingPrecio(null)}
                                              sx={{ color: '#94a3b8', '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}
                                            >
                                              <CloseIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Box>
                                        </TableCell>
                                      </>
                                    ) : (
                                      <>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                              sx={{
                                                width: 8, height: 8, borderRadius: '50%',
                                                bgcolor: getModelColor(precio.servicio),
                                                flexShrink: 0
                                              }}
                                            />
                                            <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#1e293b' }}>
                                              {precio.servicio}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={getTipoLabel(precio.tipo)}
                                            size="small"
                                            sx={{ height: 20, fontSize: 10, fontWeight: 600,
                                              bgcolor: precio.tipo === 'por_1k_tokens' ? 'rgba(99,102,241,0.08)' :
                                                       precio.tipo === 'por_unidad' ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)',
                                              color: precio.tipo === 'por_1k_tokens' ? '#6366f1' :
                                                     precio.tipo === 'por_unidad' ? '#10b981' : '#d97706'
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography sx={{ fontSize: 12, color: '#334155', fontFamily: 'monospace' }}>
                                            {precio.tipo === 'porcentaje' ? '—' : `$${precio.precio_input}`}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography sx={{ fontSize: 12, color: '#334155', fontFamily: 'monospace' }}>
                                            {precio.tipo === 'porcentaje' ? `${precio.precio_output}%` : `$${precio.precio_output}`}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                                            {precio.descripcion || '—'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                            <Tooltip title="Editar" arrow>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleEditPrecio(precio)}
                                                sx={{ color: '#6366f1', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.1)' } }}
                                              >
                                                <EditIcon sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title={precio.activo ? 'Desactivar' : 'Activar'} arrow>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleToggleActivo(precio)}
                                                sx={{
                                                  color: precio.activo ? '#94a3b8' : '#10b981',
                                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
                                                }}
                                              >
                                                {precio.activo ? (
                                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                                ) : (
                                                  <AddIcon sx={{ fontSize: 16 }} />
                                                )}
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        </TableCell>
                                      </>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      );
                    })}

                    <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 1, fontStyle: 'italic' }}>
                      Los cambios aplican inmediatamente. Cache se refresca cada 5 minutos.
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ConsumoTokens;
